import { TenantBranding } from '../types/tenant';

export interface SwitchableBranch {
    code: string;
    name: string;
    /** The group's central (root) tenant, as opposed to one of its branches. */
    isRoot: boolean;
}

/**
 * Every center of the group the user could switch to: the central first, then
 * its branches.
 *
 * P2 — this used to be derived here, from a `branding.branches` that never
 * contained the central, and the switcher was one entry short because of it: a
 * group with a central and one branch had nothing to switch between, and from
 * two or more branches there was no way back to the central. The list now comes
 * from the backend as `groupTenants`, built with the very same query that
 * decides which centers a user may access — so "where can I switch to" and
 * "what do I have access to" can't drift apart again.
 *
 * The `branches` path below is only a fallback, for a branding payload that
 * predates `groupTenants`: either a backend not promoted yet, or the copy
 * `BrandingContext` keeps in `localStorage` to avoid the first-render flash. It
 * reconstructs the central from `rootTenantCode` / `rootTenantName`, which are
 * the root's own code and name.
 */
export const switchableBranches = (branding: TenantBranding | null): SwitchableBranch[] => {
    if (!branding) return [];

    const rootCode = branding.rootTenantCode || branding.code;

    if (branding.groupTenants?.length) {
        return branding.groupTenants.map((groupTenant) => ({
            code: groupTenant.code,
            name: groupTenant.name,
            // `tenantType` has a schema default on the backend, so a document
            // older than the field could read as a root; the code comparison is
            // what actually settles it.
            isRoot: groupTenant.tenantType === 'root' || groupTenant.code === rootCode,
        }));
    }

    const rootName = branding.rootTenantName || branding.name;
    const entries: SwitchableBranch[] = rootCode
        ? [{ code: rootCode, name: rootName, isRoot: true }]
        : [];

    for (const branch of branding.branches ?? []) {
        if (entries.some((entry) => entry.code === branch.code)) continue;
        entries.push({ code: branch.code, name: branch.name, isRoot: false });
    }

    return entries;
};
